/* eslint-disable no-undef */
import { httpClient } from './httpClient.service';
import { ENDPOINTS, SERVICE_PROXY } from '../../constants/appointmentConstants';

/**
 * Servicios de dominio para el módulo de citas médicas.
 * Centraliza todas las llamadas HTTP directas al backend,
 * eliminando el acoplamiento con ServicioProxyTransar en el componente orquestador.
 */
export class AppointmentServices {
  /**
   * Verifica si el usuario tiene acceso al módulo de citas médicas.
   * @param {string} type - Tipo de identificación del usuario.
   * @param {string} id - ID del usuario.
   * @returns {Promise<object>}
   */
  static checkUserAppointmentsAccess(type, id) {
    return httpClient.get(
      `${ENDPOINTS.CHECK_ACCESS}?userTypeId=${type}&userId=${id}`,
      SERVICE_PROXY,
    );
  }

  /**
   * Obtiene la lista de afiliados del usuario (cotizante + menores beneficiarios).
   * @param {string} userId - ID del usuario.
   * @param {string} userTypeId - Tipo de identificación del usuario.
   * @returns {Promise<object>}
   */
  static getAffiliates(userId, userTypeId) {
    return httpClient.post(
      { userId, userTypeId, session: true },
      ENDPOINTS.GET_AFFILIATES,
      SERVICE_PROXY,
    );
  }

  /**
   * Obtiene el código de sede PBS para el usuario.
   * @param {string} id - ID del usuario.
   * @param {string} type - Tipo de identificación.
   * @param {string} program - Programa del usuario.
   * @returns {Promise<object>}
   */
  static getPBSBranchCode(id, type, program) {
    return httpClient.post(
      { userTypeId: type, userId: id, program },
      ENDPOINTS.GET_PBS_BRANCH,
      SERVICE_PROXY,
    );
  }

  /**
   * Obtiene las especialidades médicas permitidas para el usuario.
   * @param {object} payload - { userId, userTypeId, program }
   * @returns {Promise<object>}
   */
  static getSpecialties(payload) {
    return httpClient.post(payload, ENDPOINTS.GET_SPECIALTIES, SERVICE_PROXY);
  }

  /**
   * Verifica si el usuario tiene una sede externa asignada.
   * @param {string} userId
   * @param {string} userTypeId
   * @param {string} program
   * @returns {Promise<object>}
   */
  static checkExternalBranch(userId, userTypeId, program) {
    return httpClient.post(
      { userTypeId, userId, program },
      ENDPOINTS.CHECK_EXTERNAL_BRANCH,
      SERVICE_PROXY,
    );
  }

  /**
   * Busca disponibilidad de citas médicas para el usuario y especialidad seleccionada.
   * @param {object} payload - Objeto con los datos de la solicitud.
   * @returns {Promise<object>}
   */
  static searchAppointments(payload) {
    return httpClient.post(payload, ENDPOINTS.SEARCH_APPOINTMENTS, SERVICE_PROXY);
  }

  /**
   * Consulta el servicio V1 de direccionamiento (ruta de contingencia).
   * @param {object} payload - Objeto con los datos del usuario y especialidad.
   * @returns {Promise<object>}
   */
  static getAppointmentsV1(payload) {
    return httpClient.post(payload, ENDPOINTS.APPOINTMENTS_CHECKER_V1, SERVICE_PROXY);
  }

  /**
   * Consulta el primer control CUPS para la especialidad seleccionada.
   * Usa fetch nativo porque el endpoint es distinto al proxy estándar.
   * @param {string} userId
   * @param {string} userTypeId
   * @param {string} codCups - Código CUPS de la especialidad.
   * @returns {Promise<object>}
   */
  static getCupsControl(userId, userTypeId, codCups) {
    const url = `${document.location.origin}/_vti_bin/Servicios/ServicioTransar.svc/GetCupsFirstControl`;
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    return fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId, userTypeId, codCups }),
      redirect: 'follow',
    }).then((r) => r.json());
  }
}
